import { VueConstructor } from "vue";

import { Config } from "@/config";
import { HTTPClient } from "@/common/HTTPClient";
import { Client } from "@/clients/client";

import { AuthUseCase } from "@/features/auth/AuthUseCase";
import { ShopUseCase } from "@/features/shop/ShopUseCase";
import { ChannelUseCase } from "@/features/channel/ChannelUseCase";
import { ProductUseCase } from "@/features/product/ProductUseCase";
import { PermissionUseCase } from "@/features/permission/PermissionUseCase";
import { RoleUseCase } from "@/features/role/RoleUseCase";
import { UserUseCase } from "@/features/user/UserUseCase";
import { MigrateUseCase } from "@/features/migrate/MigrateUsecase";
import { ItemUseCase } from "@/features/item/ItemUseCase";
import { PromotionUseCase } from "@/features/promotion/PromotionUseCase";
import { OrderUseCase } from "@/features/order/OrderUseCase";
import { FixOrderUseCase } from "@/features/fix-order/FixOrderUseCase";
import { ShopifyUseCase } from "@/features/shopify/ShopifyUseCase";
import { DeliveryModeUseCase } from "@/features/delivery-modes/DeliveryModeUseCase";
import { MarketplaceUseCase } from "@/features/marketplace/MarketplaceUseCase";
import { ErrorTrackingUseCase } from "@/features/error-trackings/ErrorTrackingUseCase";
import { ShipnityUseCase } from "@/features/shipnity/ShipnityUseCase";
import { LineUseCase } from "@/features/line/LineUseCase";
import { BridgeSystemUseCase } from "./features/bridge-system/BridgeSystemUseCase";
import { ShippopUseCase } from "@/features/shippop/ShippopUseCase";
import { TMSUseCase } from "@/features/tms-shop-register/TMSUseCase";
import { ErrorEventUseCase } from "./features/notification/ErrorEventUseCase";

const { baseURL, tokenURL } = Config;

export const httpClient = new HTTPClient({ baseURL, tokenURL });
export const client = new Client(httpClient);

export const authUseCase = new AuthUseCase(client);
export const shopUseCase = new ShopUseCase(client);
export const channelUseCase = new ChannelUseCase(client);
export const productUseCase = new ProductUseCase();
export const permissionUseCase = new PermissionUseCase(client);
export const roleUseCase = new RoleUseCase(client);
export const userUseCase = new UserUseCase(client);
export const migrateUseCase = new MigrateUseCase(client);
export const itemUseCase = new ItemUseCase(client);
export const promotionUseCase = new PromotionUseCase(client);
export const orderUseCase = new OrderUseCase(client);
export const fixOrderUseCase = new FixOrderUseCase(client);
export const shopifyUseCase = new ShopifyUseCase(client);
export const deliveryModeUseCase = new DeliveryModeUseCase(client);
export const marketplaceUseCase = new MarketplaceUseCase(client);
export const errorTrackingUseCase = new ErrorTrackingUseCase(client);
export const shipnityUseCase = new ShipnityUseCase(client);
export const lineUseCase = new LineUseCase(client);
export const bridgeSystemUseCase = new BridgeSystemUseCase(client);
export const shippopUseCase = new ShippopUseCase(client);
export const tmsUseCase = new TMSUseCase(client);
export const errorEventUseCase = new ErrorEventUseCase(client);

export interface Dependency {
  httpClient: HTTPClient;
  authUseCase: AuthUseCase;
  shopUseCase: ShopUseCase;
  channelUseCase: ChannelUseCase;
  productUseCase: ProductUseCase;
  permissionUseCase: PermissionUseCase;
  migrateUseCase: MigrateUseCase;
  userUseCase: UserUseCase;
  roleUseCase: RoleUseCase;
  itemUseCase: ItemUseCase;
  promotionUseCase: PromotionUseCase;
  orderUseCase: OrderUseCase;
  fixOrderUseCase: FixOrderUseCase;
  shopifyUseCase: ShopifyUseCase;
  deliveryModeUseCase: DeliveryModeUseCase;
  marketplaceUseCase: MarketplaceUseCase;
  errorTrackingUseCase: ErrorTrackingUseCase;
  shipnityUseCase: ShipnityUseCase;
  lineUseCase: LineUseCase;
  bridgeSystemUseCase: BridgeSystemUseCase;
  shippopUseCase: ShippopUseCase;
  tmsUseCase: TMSUseCase;
  errorEventUseCase: ErrorEventUseCase;
}

export const DependencyPlugin = {
  install(Vue: VueConstructor) {
    const dep: Dependency = {
      httpClient,
      authUseCase,
      shopUseCase,
      channelUseCase,
      productUseCase,
      permissionUseCase,
      migrateUseCase,
      userUseCase,
      roleUseCase,
      itemUseCase,
      promotionUseCase,
      orderUseCase,
      fixOrderUseCase,
      shopifyUseCase,
      deliveryModeUseCase,
      marketplaceUseCase,
      errorTrackingUseCase,
      shipnityUseCase,
      lineUseCase,
      bridgeSystemUseCase,
      shippopUseCase,
      tmsUseCase,
      errorEventUseCase
    };

    Object.defineProperty(Vue.prototype, "$dep", {
      get() {
        return dep;
      }
    });
  }
};
